/**
 * Mathematical utility functions for frequency interpolation and audio calculations
 *
 * Hardened with TypeScript types and additional helper exports:
 * - computeQForFrequencies(freqs)
 *
 * Backwards-compatible exports are preserved.
 */

import type { FrequencyBand } from '../types/math-utils';
import type { EQSetting } from '../types/index';

// ISO contour offset used in iso226.json (original app stores contour with +90 dB offset)
const ISO_CONTOUR_OFFSET = 90;

type MeasuredPoint = { frequency: number; db: number | null } | [number, number];
type FillPresetOptions = { strategy?: 'linear-frequency' | 'log-frequency' | 'nearest' | 'leave-empty'; epsilon?: number; extrapolate?: boolean };

/**
 * Get axis value for frequency interpolation
 * @param freq Frequency in Hz
 * @param mode 'log' or 'linear' (may pass other strings for backward compat but 'log'/'linear' expected)
 * @param defaultMode Default interpolation mode to use if mode is not provided
 */
function axisValue(freq: number, mode: string, defaultMode = 'log'): number {
    const m = mode || defaultMode;
    if (m === 'log') return Math.log10(freq);
    return freq;
}

/**
 * Interpolate value at frequency between two points
 */
function interpAt(frequency: number, p1: MeasuredPoint | undefined, p2: MeasuredPoint | undefined, mode: string, defaultMode = 'log'): number {
    // extract frequency and value helpers
    function freqOf(p: MeasuredPoint | undefined): number | undefined {
        if (!p) return undefined;
        return Array.isArray(p) ? p[0] : (p as { frequency?: number }).frequency;
    }
    function valOf(p: MeasuredPoint | undefined): number | undefined {
        if (!p) return undefined;
        return Array.isArray(p) ? p[1] : (p as { db?: number | null }).db ?? undefined;
    }

    const v1 = valOf(p1);
    const v2 = valOf(p2);
    const f1 = freqOf(p1);
    const f2 = freqOf(p2);

    if (v1 === undefined && v2 === undefined) return 0;
    if (v1 === undefined) return v2!;
    if (v2 === undefined) return v1!;
    if (f1 === undefined && f2 === undefined) return (v1 + v2) / 2;
    if (f1 === undefined) return v2!;
    if (f2 === undefined) return v1!;

    const x1 = axisValue(f1, mode, defaultMode);
    const x2 = axisValue(f2, mode, defaultMode);
    const x = axisValue(frequency, mode, defaultMode);

    if (x2 === x1) return (v1 + v2) / 2;
    const t = (x - x1) / (x2 - x1);
    return v1 + t * (v2 - v1);
}

/**
 * Convert dB to linear gain used by WebAudio nodes
 */
function dbToGain(db: number): number {
    if (typeof db !== 'number') db = Number(db) || 0;
    if (db <= -100) return 0;
    return Math.pow(10, db / 20);
}

/**
 * Convert linear gain to dB
 */
function gainToDb(gain: number): number {
    if (typeof gain !== 'number') gain = Number(gain) || 0;
    if (gain <= 0) return -100;
    return 20 * Math.log10(gain);
}

/**
 * Fill missing preset frequencies using interpolation
 */
function fillPresetFrequencies(measuredPoints: MeasuredPoint[], presetFrequencies: number[], options: FillPresetOptions = {}): Record<number, number | null> {
    options = options || {};
    const strategy = options.strategy || 'linear-frequency'; // default
    const epsilon = (typeof options.epsilon === 'number') ? options.epsilon : 1e-6;
    const extrapolate = !!options.extrapolate;

    if (!Array.isArray(measuredPoints) || measuredPoints.length === 0) {
        const outEmpty: Record<number, number | null> = {};
        presetFrequencies.forEach((f: number) => outEmpty[f] = null);
        return outEmpty;
    }

    // Normalize measured points to objects {frequency, db}
    const measured = measuredPoints.slice().map(p => {
        if (Array.isArray(p)) {
            return { frequency: p[0], db: p[1] as number | null };
        }
        return { frequency: (p as { frequency: number }).frequency, db: (p as { db?: number | null }).db ?? null };
    }) as { frequency: number; db: number | null }[];
    measured.sort((a, b) => a.frequency - b.frequency);

    const result: Record<number, number | null> = {};

    presetFrequencies.forEach((f: number) => {
        // exact match check
        const exact = measured.find(p => Math.abs(p.frequency - f) <= epsilon);
        if (exact) {
            result[f] = exact.db;
            return;
        }

        if (strategy === 'leave-empty') {
            result[f] = null;
            return;
        }

        // find neighbors
        let lower: {frequency:number, db:number|null} | null = null;
        let upper: {frequency:number, db:number|null} | null = null;
        for (let i = 0; i < measured.length; i++) {
            const p = measured[i];
            if (p.frequency < f) lower = p;
            if (p.frequency > f) { upper = p; break; }
        }

        if (strategy === 'nearest') {
            // choose nearest by absolute frequency distance
            let nearest = measured[0];
            let bestDiff = Math.abs(measured[0].frequency - f);
            for (let i = 1; i < measured.length; i++) {
                const d = Math.abs(measured[i].frequency - f);
                if (d < bestDiff) { bestDiff = d; nearest = measured[i]; }
            }
            result[f] = nearest.db;
            return;
        }

        if (lower && upper) {
            // interpolate between lower and upper
            if (strategy === 'log-frequency') {
                const x1 = Math.log10(lower.frequency);
                const x2 = Math.log10(upper.frequency);
                const x = Math.log10(f);
                if (Math.abs(x2 - x1) < 1e-12) {
                    result[f] = lower.db;
                } else {
                    const t = (x - x1) / (x2 - x1);
                    result[f] = (lower.db ?? 0) + t * ((upper.db ?? 0) - (lower.db ?? 0));
                }
            } else {
                const denom = axisValue(upper.frequency, strategy as string) - axisValue(lower.frequency, strategy as string);
                if (Math.abs(denom) < 1e-12) {
                    result[f] = lower.db;
                } else {
                    const t = (axisValue(f, strategy as string) - axisValue(lower.frequency, strategy as string)) / denom;
                    result[f] = (lower.db ?? 0) + t * ((upper.db ?? 0) - (lower.db ?? 0));
                }
            }
            return;
        }

        // only one neighbor exists
        const neighbor = lower || upper;
        if (!neighbor) {
            result[f] = null;
            return;
        }

        if (extrapolate) {
            result[f] = neighbor.db;
        } else {
            result[f] = neighbor.db;
        }
    });

    return result;
}

/**
 * Extract all gain values from EQ settings
 */
function getAllGainValues(eqSettings: Partial<EQSetting>[]): number[] {
    if (!Array.isArray(eqSettings)) {
        return [];
    }

    return eqSettings
        .filter((setting): setting is Partial<EQSetting> & { gain: number } => 
            setting != null && typeof setting.gain === 'number' && isFinite(setting.gain))
        .map((setting) => setting.gain);
}

/**
 * Validate EQ settings structure
 */
function validateEqSettings(eqSettings: unknown[]): eqSettings is Partial<EQSetting>[] {
    if (!Array.isArray(eqSettings) || eqSettings.length === 0) {
        return false;
    }

    // Check if at least one setting has a valid gain value
    return eqSettings.some((setting: unknown) =>
        setting != null &&
        typeof setting === 'object' &&
        'gain' in setting &&
        typeof (setting as any).gain === 'number' &&
        isFinite((setting as any).gain)
    );
}

/**
 * Compute Q (filter width) values for an ordered list of frequencies.
 *
 * Mirrors Peace's quality calculation (Peace16911.au3:6252-6260) exactly so the
 * generated filter shapes match the original app, not just the gains at band centers:
 *   first band:  Q = 2^(f[0]/f[1])
 *   last band:   Q = 2^(f[n-2]/f[n-1])
 *   middle band: Q = 2^((f[i-1]/f[i] + f[i]/f[i+1]) / 2)
 * then every band is divided by sqrt(2)^(peakQuality - 2). Peace's default
 * peakQuality is 0, giving a divisor of 0.5.
 *
 * @param freqs frequencies in Hz (any order; sorted ascending internally)
 * @param peakQuality Peace's "Equalization Peak Quality" index (default 0)
 */
function computeQForFrequencies(freqs: number[], peakQuality = 0): number[] {
    const DEFAULT_Q = 1.41;
    if (!Array.isArray(freqs) || freqs.length === 0) return [];

    const sorted = freqs.slice().sort((a, b) => a - b);
    const n = sorted.length;

    if (n === 1) {
        return [DEFAULT_Q];
    }

    const divisor = Math.pow(Math.sqrt(2), peakQuality - 2);
    const qs: number[] = [];

    for (let i = 0; i < n; i++) {
        let exponent: number;
        if (i === 0) {
            exponent = sorted[0] / sorted[1];
        } else if (i === n - 1) {
            exponent = sorted[n - 2] / sorted[n - 1];
        } else {
            exponent = (sorted[i - 1] / sorted[i] + sorted[i] / sorted[i + 1]) / 2;
        }

        let q = Math.pow(2, exponent) / divisor;
        if (!isFinite(q) || q <= 0) q = DEFAULT_Q;
        qs.push(q);
    }

    return qs;
}

// ES Module Exports (preserve original names)
export { ISO_CONTOUR_OFFSET, axisValue, interpAt, fillPresetFrequencies, dbToGain, gainToDb, getAllGainValues, validateEqSettings, computeQForFrequencies };
export default {
    axisValue,
    interpAt,
    fillPresetFrequencies,
    dbToGain,
    gainToDb,
    getAllGainValues,
    validateEqSettings,
    computeQForFrequencies
};
